import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { ClosingCTA } from '../components/ClosingCTA';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { PipelineBoardVisual } from '../components/visuals/PipelineBoardVisual';
import { ConsolidationVisual } from '../components/visuals/ConsolidationVisual';
import { ReengageVisual } from '../components/visuals/ReengageVisual';
import { SplitSection, ChecklistSection, ProofStrip, FaqSection, PriceSection, RelatedLinks, serviceJsonLd } from '../components/LocalPage';

/* 2026-09-17: money page 4 of 5. Target query: "crm for contractors"
   (390 a month, KD 31). The old /crm-business-tools route still redirects
   to /how-it-works; this page is the new exact-match home for the query.
   No reviews, no reputation, no $1,197. */

const faqs = [{
  question: 'What is a CRM for contractors?',
  answer: 'A place where every lead, customer, call, text, estimate and invoice lives, so nothing depends on your memory or a notebook on the dash. For a contractor the useful part is not the database. It is the follow-up that runs on its own, the calendar that fills itself, and the invoice that gets chased without you asking twice.'
}, {
  question: 'I tried a CRM before and stopped using it. Why would this be different?',
  answer: 'Because you do not have to feed it. Calls, texts, Yelp messages, Google messages and web forms land in it automatically, the AI receptionist books into it, and the follow-ups run from it without anyone typing. The CRMs contractors abandon are the ones that only work if you sit at a desk and update them. This one works from the truck.'
}, {
  question: 'Does it work with Yelp and Google?',
  answer: 'Yes. Yelp quote requests, Google Business messages, Facebook and Instagram messages, calls, texts and web forms all arrive in one inbox. You reply from one screen, and the reply goes back out on whichever channel the customer used.'
}, {
  question: 'Can I send estimates and invoices from it?',
  answer: 'Yes. Estimates, invoices and payment links go out by text or email, and the CRM syncs with QuickBooks. An unpaid invoice gets a reminder on its own until it is paid.'
}, {
  question: 'Is there a mobile app?',
  answer: 'Yes. The inbox, the calendar, the pipeline and your contacts are all on your phone. Most of our clients never open it on a computer.'
}, {
  question: 'How much does a CRM for contractors cost?',
  answer: 'The CRM comes as part of The Viridia System at $897 a month, month to month, with the AI receptionist, missed-call text-back, a business number and the automations. We set it up with you and build your pipeline and follow-ups on a setup call. It is not a login and a video course.'
}];
const jsonLd = serviceJsonLd({
  name: 'CRM for Contractors',
  serviceType: 'CRM setup and automation for contractors',
  description: 'A CRM for contractors with one inbox for calls, texts, Yelp and Google, a job pipeline, follow-ups that run on their own, and invoicing with QuickBooks sync. Set up with you as part of The Viridia System, $897 a month.',
  path: '/crm-for-contractors'
});
export function CrmForContractors() {
  return <>
      <SEOHead title="CRM for Contractors That Runs Itself | One Inbox, Follow-Up, Invoicing | Viridia" description="A CRM for contractors built for the truck, not the desk. One inbox for calls, texts, Yelp and Google, a job pipeline, follow-ups that run on their own, and invoicing with QuickBooks sync. Set up with you. $897 a month." canonical="/crm-for-contractors" jsonLd={jsonLd} faq={faqs} data-id="element-2387" data-mp="70px924wf2z3" />

      <PageHeader label="Reception" title="A CRM for contractors who do not want to run a CRM." intro="Every call, text, Yelp lead and web form in one inbox. A pipeline that shows where each job is. Follow-ups and invoice reminders that go out on their own. Built with you, so it works from the truck." data-id="element-2388" data-mp="2htj4rcrfw8ot">
        <div className="flex flex-wrap items-center gap-4" data-id="element-2389" data-mp="3rpehkryscv9n">
          <CTAButton to="/contact" data-id="element-2390" data-mp="3rqzbqgh6uc6z">Book a 15-min call</CTAButton>
          <CTAButton to="/what-it-fixes" variant="ghost" data-id="element-2391" data-mp="jwm3th00dyv">
            What it fixes
          </CTAButton>
        </div>
      </PageHeader>

      <SplitSection label="One inbox" title="Stop checking five apps at 9 PM." body={<>
            <p data-id="element-2393" data-mp="ea9qjluolajg">
              Calls, texts, Yelp quote requests, Google messages, Facebook, Instagram and your
              website form all land in one inbox. You reply from one screen and it goes back out on
              whichever channel the customer used.
            </p>
            <p data-id="element-2394" data-mp="2z2lfa55qznxj">The AI receptionist and missed-call text-back write into the same place.</p>
          </>} visual={<ConsolidationVisual data-id="element-2395" data-mp="1m1ymtvtb1aie" />} note="Unified inbox" data-id="element-2392" data-mp="o97hevfkxg5m" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-2396" data-mp="1m0448o5i9uhh">
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-2397" data-mp="1h2lyt7qp5xe8">
          <div className="max-w-2xl" data-id="element-2398" data-mp="1v1c06zkjuj8e">
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-emerald-500" data-id="element-2399" data-mp="1v0aocsvplspm">
              <span className="h-px w-6 bg-emerald-500/60" aria-hidden="true" data-id="element-2400" data-mp="13bh14b6w3f6j" />
              The pipeline
            </p>
            <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2401" data-mp="yd5nrqhdymjk">
              Every job, and where it is, on one board.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-neutral-400" data-id="element-2402" data-mp="17dawc3ybfqzb">
              New lead, estimate sent, scheduled, done. A job moves across the board as it happens,
              so you know who is waiting on a quote and who is waiting on you without scrolling
              through texts.
            </p>
          </div>
          <div className="mt-12" data-id="element-2403" data-mp="2eplddl3xrc8b">
            <PipelineBoardVisual data-id="element-2404" data-mp="2eqcz847qdvle" />
          </div>
        </div>
      </section>

      <SplitSection label="Follow-up" title="Nobody goes cold because you got busy." body={<>
            <p data-id="element-2406" data-mp="2oisg0y7ake4p">
              An estimate that has not been answered gets a nudge in two days. A lead that said
              maybe hears from you next week. An invoice that sits unpaid gets a reminder on its
              own.
            </p>
            <p data-id="element-2407" data-mp="20q27z02ieezp">You set the rules once on the setup call. The CRM does the remembering.</p>
          </>} visual={<DeviceMockup mode="chat" heading="Estimate #2117, day 2" lines={[{
      from: 'system',
      text: 'Hi Maria, checking in on the patio estimate we sent Tuesday. Any questions before we lock in a start date?'
    }, {
      from: 'customer',
      text: 'Sorry, been slammed. Yes, let us do it. When can you start?'
    }]} resultChip="Job won on the follow-up" data-id="element-2408" data-mp="25odhtast0phx" />} note="Automations" data-id="element-2405" data-mp="2joeq3bbeez7f" />

      <SplitSection alt flip label="Invoicing" title="Estimates, invoices and payment links from the same place." body={<>
            <p data-id="element-2410" data-mp="vsqf87thfdy9">
              Send the estimate by text while you are still in the driveway. Convert it to an
              invoice when the job is done. The payment link goes out with it, and it all syncs to
              QuickBooks.
            </p>
            <p data-id="element-2411" data-mp="mtd1h2ybs37q">The unpaid ones get chased without you being the one who has to ask twice.</p>
          </>} visual={<DeviceMockup mode="chat" heading="Invoice #1842" lines={[{
      from: 'system',
      text: 'Reminder: invoice #1842 for $640 is due Friday. Pay here: link'
    }, {
      from: 'customer',
      text: 'Missed this, paying now. Thanks.'
    }]} resultChip="Paid, synced to QuickBooks" data-id="element-2412" data-mp="ctm9b7x8m0at" />} note="QuickBooks sync" data-id="element-2409" data-mp="15j2x1s9wwt0p" />

      <ChecklistSection label="What is in it" title="What the CRM does for a contractor." columns={3} items={[{
      title: 'One inbox for every channel',
      body: 'Calls, texts, Yelp, Google, Meta and web forms in one thread per customer.'
    }, {
      title: 'A job pipeline',
      body: 'New lead to done, on a board you can read in five seconds from the truck.'
    }, {
      title: 'Calendar and booking link',
      body: 'The receptionist books into it. Customers can book into it themselves. Reminders go out before every appointment.'
    }, {
      title: 'Follow-ups that run themselves',
      body: 'Estimate nudges, maybe-later reminders, invoice reminders, all on rules you set once.'
    }, {
      title: 'Estimates, invoices and payments',
      body: 'Sent by text or email, paid by link, synced to QuickBooks.'
    }, {
      title: 'Mobile app',
      body: 'Everything above, on your phone. Most clients never touch the desktop version.'
    }]} data-id="element-2413" data-mp="1l148cwgls93z" />

      <SplitSection alt label="Old customers" title="Your contact list is the cheapest lead source you have." body={<>
            <p data-id="element-2415" data-mp="kuh5jf3lxwzk">
              Every customer you have ever served is in one list. A short campaign before the busy
              season reminds them you exist, and the replies land in the same inbox.
            </p>
            <p data-id="element-2416" data-mp="ps0w9ehhs28e">Most contractors have hundreds of these contacts in their phone and never use them.</p>
          </>} visual={<ReengageVisual data-id="element-2417" data-mp="13qh4j9v060sn" />} note="Campaigns" data-id="element-2414" data-mp="64jf0si7gv77" />

      <ProofStrip items={[{
      value: '284%',
      label: 'more leads for a Phoenix landscaping company after the system went in',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '39.95%',
      label: 'close rate on leads run through the pipeline in that account',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '$15k',
      label: 'closed-won painting work traced through the CRM',
      to: '/case-studies/finish-coat-painting',
      source: 'Finish Coat Painting'
    }]} data-id="element-2418" data-mp="ysfjije0nrej" />

      <PriceSection title="The CRM comes set up, with the receptionist attached." body={<p data-id="element-2420" data-mp="2ayda1p01vag5">
            We build your pipeline, your follow-ups and your templates with you on a setup call.
            The AI receptionist, missed-call text-back and a business number are in the same
            price.
          </p>} data-id="element-2419" data-mp="2eynpds9qfd7o" />

      <FaqSection alt title="Questions contractors ask about the CRM." faqs={faqs} data-id="element-2421" data-mp="2xwhtbrw06yj7" />

      <RelatedLinks title="Related pages" links={[{
      to: '/what-it-fixes',
      label: 'What it fixes',
      body: 'The six things that cost you money when nobody is watching them.'
    }, {
      to: '/answering-service-for-contractors',
      label: 'Answering service for contractors',
      body: 'What feeds the CRM: the receptionist that books the job.'
    }, {
      to: '/how-it-works',
      label: 'How it works',
      body: 'Every piece that gets set up, and what each one does for you.'
    }]} data-id="element-2422" data-mp="2tvo6l1uarprw" />

      <ClosingCTA data-id="element-2423" data-mp="1h4d06rb56pod" />
    </>;
}